// =============================================================================
// UserChip.tsx — Patch 009 header right-slot identity affordance.
//
// Avatar circle (gold-bordered, initials from CF Access email) + dropdown:
//   - "Signed in as <email>" (full email, mono small)
//   - Sign out → /cdn-cgi/access/logout (Cloudflare Access logout endpoint)
//
// Click outside the dropdown closes it. Escape also closes. No account
// settings, no profile — that's Patch 010+ scope.
// =============================================================================

import { useEffect, useRef, useState } from 'react'

import { useMe } from '../lib/me'

const CLOUDFLARE_ACCESS_LOGOUT = '/cdn-cgi/access/logout' as const

export function UserChip(): JSX.Element | null {
  const me = useMe()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent): void {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (me.kind !== 'ready') return null
  const { email, initials } = me.me

  return (
    <div className="hcc-user-chip" ref={containerRef} data-testid="user-chip">
      <button
        type="button"
        className="hcc-user-chip__avatar"
        aria-label={`Signed in as ${email}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid="user-chip-avatar"
      >
        {initials}
      </button>
      {open ? (
        <div className="hcc-user-chip__menu" role="menu" data-testid="user-chip-menu">
          <div className="hcc-user-chip__menu-row hcc-user-chip__menu-row--info">
            <span className="hcc-user-chip__menu-label">Signed in as</span>
            <code className="hcc-user-chip__menu-email">{email}</code>
          </div>
          <a
            className="hcc-user-chip__menu-row hcc-user-chip__menu-row--action"
            href={CLOUDFLARE_ACCESS_LOGOUT}
            role="menuitem"
            data-testid="user-chip-signout"
          >
            Sign out
          </a>
        </div>
      ) : null}
    </div>
  )
}
